import React, { useContext, useState, useEffect } from "react";
import { DataContext } from "../context/DataContext";
import EventCardParent from "./EventCardParent";
import Select from "react-select";
import { useNavigate, Link } from "react-router-dom";
import { FaPlusCircle, FaArrowUp, FaSearch, FaTimes, FaList, FaTh, FaTable } from "react-icons/fa";
import LoadingScreen from "../utilities/LoadingScreen";

const EventTable = ({ Dashboard, showSponsoredOnly }) => {
  const { eventData, sponsorCategory, loading, fetchAllEvents } = useContext(DataContext);
  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState({ value: "ongoing", label: "Ongoing" });
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [viewMode, setViewMode] = useState("table"); // 'list', 'grid', 'table'
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const navigate = useNavigate();

  const handleCreateEvent = () => navigate("/event-form");

  // Scroll to Top function
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  // Show scroll button when scrolled down
  useEffect(() => {
    const handleScroll = () => setShowScrollButton(window.scrollY > 100);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Fetch events when filter changes
  useEffect(() => {
    const loadEvents = async () => {
      setIsFilterLoading(true);
      await fetchAllEvents(filter.value, undefined, undefined, true);
      setIsFilterLoading(false);
    };
    loadEvents();
  }, [filter, fetchAllEvents]);

  // Filter and search logic
  useEffect(() => {
    if (!eventData) return;

    // Step 1: Filter sponsored events if required
    let filtered = [...eventData];
    if (showSponsoredOnly) {
      const sponsoredEventIds = new Set(
        sponsorCategory.map((category) => category.eventId)
      );
      filtered = filtered.filter((event) =>
        sponsoredEventIds.has(event.eventID)
      );
    }

    // Step 2: Apply search query if it exists
    const trimmedSearchQuery = searchQuery.trim().toLowerCase();
    if (trimmedSearchQuery) {
      filtered = filtered.filter((event) =>
        event.eventName.toLowerCase().includes(trimmedSearchQuery)
      );
    }

    setFilteredEvents(filtered);
  }, [eventData, searchQuery, showSponsoredOnly, sponsorCategory]);

  // Filter options from API
  const filterOptions = [
    { value: "all", label: "All Events" },
    { value: "ongoing", label: "Ongoing" },
    { value: "pastweekevents", label: "Past Week Events" },
    { value: "pastmonthevents", label: "Past Month Events" },
    { value: "pastyearevents", label: "Past Year Events" },
    { value: "upcoming", label: "Upcoming" },
    { value: "thismonth", label: "This Month" },
    { value: "thisyear", label: "This Year" },
  ];

  return (
    <div className="lg:p-2">
      {loading ? (
        <LoadingScreen />
      ) : (
        <>
          {/* Filters Row */}
          <div className="flex flex-wrap items-center gap-4 mb-6">
            {/* Create Event Button */}
            {!Dashboard && (
              <button
                onClick={handleCreateEvent}
                className="flex items-center bg-blue-500 text-white py-2 px-4 rounded-lg shadow-lg hover:bg-blue-600 transition"
              >
                <FaPlusCircle className="mr-2" />
                Create Event
              </button>
            )}

            {/* Status Filter */}
            <Select
              options={filterOptions}
              value={filter}
              onChange={(selectedOption) => setFilter(selectedOption)}
              className="w-48"
            />

            {/* View Mode Toggle */}
            <div className="flex items-center bg-white border border-gray-300 rounded-lg shadow-sm">
              <button
                onClick={() => setViewMode("list")}
                className={`p-2 px-3 transition ${
                  viewMode === "list"
                    ? "bg-blue-500 text-white"
                    : "text-gray-600 hover:bg-gray-100"
                } rounded-l-lg`}
                title="List View"
              >
                <FaList />
              </button>
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2 px-3 transition ${
                  viewMode === "grid"
                    ? "bg-blue-500 text-white"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
                title="Grid View"
              >
                <FaTh />
              </button>
              <button
                onClick={() => setViewMode("table")}
                className={`p-2 px-3 transition ${
                  viewMode === "table"
                    ? "bg-blue-500 text-white"
                    : "text-gray-600 hover:bg-gray-100"
                } rounded-r-lg`}
                title="Table View"
              >
                <FaTable />
              </button>
            </div>

            {/* Search Input */}
            <div className="relative flex-1 min-w-[300px]">
              <div className="flex items-center bg-white border border-gray-300 rounded-full shadow-sm">
                <input
                  type="text"
                  placeholder="Search events..."
                  className="w-full text-gray-700 bg-transparent px-4 py-3 rounded-full"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="text-gray-500 h-full px-3 flex items-center"
                    style={{ position: "absolute", right: "45px" }}
                  >
                    <FaTimes />
                  </button>
                )}
                <button
                  className="text-gray-500 h-full px-3 flex items-center rounded-r-full absolute right-4 lg:right-4"
                  aria-label="Search"
                >
                  <FaSearch />
                </button>
              </div>
            </div>
          </div>

          {/* Event Cards - List View */}
          {viewMode === "list" && (
            <div className="grid grid-cols-1 gap-6">
              {isFilterLoading ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
                  <p className="text-gray-500">Loading events...</p>
                </div>
              ) : (
                <EventCardParent events={filteredEvents} />
              )}
            </div>
          )}

          {/* Grid View - 3 items per row */}
          {viewMode === "grid" && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {isFilterLoading ? (
                <div className="col-span-full flex flex-col items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
                  <p className="text-gray-500">Loading events...</p>
                </div>
              ) : filteredEvents.length === 0 ? (
                <div className="col-span-full text-center py-16 text-gray-500">
                  No events found
                </div>
              ) : (
                filteredEvents.map((event, index) => (
                  <div
                    key={event.eventID || event.eventId || event.id || index}
                    className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow overflow-hidden"
                  >
                  {/* Event Number Badge */}
                  <div className="relative">
                    <div className="absolute top-2 left-2 bg-blue-500 text-white text-xs w-8 h-8 flex items-center justify-center rounded-full font-semibold z-10">
                      {index + 1}
                    </div>
                    {/* Event Image or Placeholder */}
                    <div className="h-40 bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center">
                      {event.eventImage ? (
                        <img
                          src={event.eventImage}
                          alt={event.eventName}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-white text-4xl font-bold opacity-50">
                          {event.eventName?.charAt(0) || "E"}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Event Details */}
                  <div className="p-4">
                    <Link
                      to={`/event-form/${event.eventID}`}
                      state={{ event }}
                      className="text-lg font-semibold text-blue-700 hover:text-blue-900 hover:underline mb-2 line-clamp-2 block transition"
                    >
                      {event.eventName}
                    </Link>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-3 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                        {event.status || "Active"}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 mb-3">
                      <p className="flex items-center gap-2 mb-1">
                        <span className="font-medium">Start:</span>
                        {event.startDate
                          ? new Date(event.startDate).toLocaleDateString()
                          : "TBD"}
                      </p>
                      <p className="flex items-center gap-2">
                        <span className="font-medium">End:</span>
                        {event.endDate
                          ? new Date(event.endDate).toLocaleDateString()
                          : "TBD"}
                      </p>
                    </div>
                    <Link
                      to={`/event-form/${event.eventID}`}
                      state={{ event }}
                      className="w-full bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition block text-center"
                    >
                      Edit Event
                    </Link>
                  </div>
                </div>
                ))
              )}
            </div>
          )}

          {/* Table View */}
          {viewMode === "table" && (
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        #
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Event Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Duration
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {isFilterLoading ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-16 text-center">
                          <div className="flex flex-col items-center justify-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
                            <p className="text-gray-500">Loading events...</p>
                          </div>
                        </td>
                      </tr>
                    ) : filteredEvents.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-16 text-center text-gray-500">
                          No events found
                        </td>
                      </tr>
                    ) : (
                      filteredEvents.map((event, index) => (
                        <tr
                          key={event.eventID || event.eventId || event.id || index}
                          className="hover:bg-gray-50 transition"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center justify-center w-8 h-8 bg-blue-500 text-white rounded-full text-sm font-semibold">
                              {index + 1}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <Link
                              to={`/event-form/${event.eventID}`}
                              state={{ event }}
                              className="text-sm font-medium text-blue-700 hover:text-blue-900 hover:underline transition"
                            >
                              {event.eventName}
                            </Link>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {(() => {
                              const isPublished = event.additionalData?.isPublish !== undefined
                                ? event.additionalData?.isPublish
                                : false;
                              return (
                                <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  isPublished
                                    ? "bg-green-100 text-green-800"
                                    : "bg-red-100 text-red-700"
                                }`}>
                                  {isPublished ? "Published" : "Unpublished"}
                                </span>
                              );
                            })()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              (event.eventType || "").toLowerCase() === "long"
                                ? "bg-purple-100 text-purple-800"
                                : "bg-blue-100 text-blue-800"
                            }`}>
                              {(event.eventType || "").toLowerCase() === "long" ? "Long" : "Short"}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {event.startDate
                              ? new Date(event.startDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
                              : "TBD"}
                            {" — "}
                            {event.endDate
                              ? new Date(event.endDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
                              : "TBD"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <Link
                              to={`/event-form/${event.eventID}`}
                              state={{ event }}
                              className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition inline-block"
                            >
                              Edit
                            </Link>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Scroll Button */}
          {showScrollButton && (
            <button
              onClick={scrollToTop}
              className="fixed bottom-4 right-4 bg-blue-100 p-3 rounded-full shadow-lg"
              aria-label="Scroll to Top"
            >
              <FaArrowUp />
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default EventTable;





// import React, { useContext, useState, useEffect } from "react";
// import { DataContext } from "../context/DataContext";
// import EventCardParent from "./EventCardParent";
// import Select from "react-select";
// import { useNavigate } from "react-router-dom";
// import {
//   FaPlusCircle,
//   FaArrowUp,
//   FaSearch,
//   FaTimes,
// } from "react-icons/fa";
// import LoadingScreen from "../utilities/LoadingScreen";

// const EventTable = ({ Dashboard }) => {
//   const { eventData, loading } = useContext(DataContext);
//   const [searchQuery, setSearchQuery] = useState("");
//   const [filter, setFilter] = useState({ value: "All", label: "All Events" });
//   const [filteredEvents, setFilteredEvents] = useState([]);
//   const [showScrollButton, setShowScrollButton] = useState(false);
//   const navigate = useNavigate();

//   if (loading) <LoadingScreen />;

//   const handleCreateEvent = () => navigate("/event-form");

//   // Scroll to Top function
//   const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

//   // Show scroll button when scrolled down
//   useEffect(() => {
//     const handleScroll = () => setShowScrollButton(window.scrollY > 100);
//     window.addEventListener("scroll", handleScroll);
//     return () => window.removeEventListener("scroll", handleScroll);
//   }, []);

//   // Filter and search logic
//   useEffect(() => {
//     if (!eventData) return;

//     const now = new Date();

//     // Step 1: Apply filter type (Ongoing, Upcoming, Completed)
//     const filteredByType = eventData.filter((event) => {
//       const startDate = new Date(event.startDate);
//       const endDate = new Date(event.endDate);

//       if (filter.value === "Ongoing") {
//         return startDate <= now && endDate >= now;
//       }
//       if (filter.value === "Upcoming") {
//         return startDate > now;
//       }
//       if (filter.value === "Completed") {
//         return endDate < now;
//       }
//       return true; // for "All" events
//     });

//     // Step 2: Apply search query if it exists
//     const trimmedSearchQuery = searchQuery.trim().toLowerCase();
//     const fullyFilteredEvents = trimmedSearchQuery
//       ? filteredByType.filter((event) =>
//           event.eventName.toLowerCase().includes(trimmedSearchQuery)
//         )
//       : filteredByType;

//     setFilteredEvents(fullyFilteredEvents);
//   }, [eventData, filter, searchQuery]);

//   // Filter options
//   const filterOptions = [
//     { value: "All", label: "All Events" },
//     { value: "Ongoing", label: "Ongoing" },
//     { value: "Upcoming", label: "Upcoming" },
//     { value: "Completed", label: "Completed" },
//   ];

//   return (
//     <div className="lg:p-4 ">
//       <div className="flex lg:flex-row flex-col space-y-4 lg:space-y-0 items-center justify-between mb-6 space-x-">
//         {/* Create Button */}
//         <div className=" grid grid-cols-2 gap-4">
//         {!Dashboard && (
//           <button
//             onClick={handleCreateEvent}
//             className="flex items-center bg-blue-500 text-white py-2 px-4 rounded-lg shadow-lg hover:bg-blue-600 transition"
//           >
//             <FaPlusCircle className="mr-2" />
//             Create Event
//           </button>
//         )}

//         {/* Filter Select */}
//         <Select
//           options={filterOptions}
//           value={filter}
//           onChange={(selectedOption) => setFilter(selectedOption)}
//           className="w-40"
//         />
//        </div>
//         {/* Search Input */}
//         <div className="relative w-full px-2 md:w-[80%] lg:w-1/2 ">
//           <div className="flex items-center bg-white border border-gray-300 rounded-full shadow-sm">
//             <input
//               type="text"
//               placeholder="Search events..."
//               className="w-full text-gray-700 bg-transparent px-4 py-3 rounded-full"
//               value={searchQuery}
//               onChange={(e) => setSearchQuery(e.target.value)}
//             />
//             {searchQuery && (
//               <button
//                 onClick={() => setSearchQuery("")}
//                 className="text-gray-500 h-full px-3 flex items-center"
//                 style={{ position: "absolute", right: "45px" }}
//               >
//                 <FaTimes />
//               </button>
//             )}
     
//             <button
//               onClick={() => setFilteredEvents(filteredEvents)}
//               className="text-gray-500 h-full px-3 flex items-center rounded-r-full absolute right-4 lg:right-4" 
//             >
//               <FaSearch />
//             </button>
//           </div>
//         </div>
//       </div>

//       {/* Event Cards */}
//       <div className="grid grid-cols-1 gap-6">
//         <EventCardParent events={filteredEvents}/>
//       </div>

//       {/* Scroll Button */}
//       {showScrollButton && (
//         <button
//           onClick={scrollToTop}
//           className="fixed bottom-4 right-4 bg-gray-200 p-3 rounded-full shadow-lg"
//           aria-label="Scroll to Top"
//         >
//           <FaArrowUp />
//         </button>
//       )}
//     </div>
//   );
// };

// export default EventTable;










// import React, { useContext, useState, useEffect } from "react";
// import { DataContext } from "../context/DataContext";
// import EventCard from "./EventCard";
// import Select from "react-select";
// import { useNavigate } from "react-router-dom";
// import {
//   FaPlusCircle,
//   FaArrowUp,
//   FaSearch,
//   FaTimes,
//   FaArrowLeft,
// } from "react-icons/fa";
// import LoadingScreen from "../utilities/LoadingScreen";
// import EventCardParent from "./EventCardParent";

// const EventTable = ({ Dashboard }) => {
//   const { eventData, loading } = useContext(DataContext);
//   if (loading) <LoadingScreen />;
//   const [searchQuery, setSearchQuery] = useState("");
//   const [filter, setFilter] = useState({ value: "All", label: "All Events" });
//   const [filteredEvents, setFilteredEvents] = useState([]);
//   const [showScrollButton, setShowScrollButton] = useState(false);
//   const navigate = useNavigate();

//   const handleCreateEvent = () => navigate("/event-form");

//   // Scroll to Top function
//   const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

//   // Show scroll button when scrolled down
//   useEffect(() => {
//     const handleScroll = () => setShowScrollButton(window.scrollY > 100);
//     window.addEventListener("scroll", handleScroll);
//     return () => window.removeEventListener("scroll", handleScroll);
//   }, []);

//   // Filter and search logic
//   useEffect(() => {
//     if (!eventData) return;

//     const now = new Date();

//     // Step 1: Apply filter type (Ongoing, Upcoming, Completed)
//     const filteredByType = eventData.filter((event) => {
//       const startDate = new Date(event.startDate);
//       const endDate = new Date(event.endDate);

//       if (filter.value === "Ongoing") {
//         return startDate <= now && endDate >= now;
//       }
//       if (filter.value === "Upcoming") {
//         return startDate > now;
//       }
//       if (filter.value === "Completed") {
//         return endDate < now;
//       }
//       return true; // for "All" events
//     });

//     // Step 2: Apply search query if it exists
//     const trimmedSearchQuery = searchQuery.trim().toLowerCase();
//     const fullyFilteredEvents = trimmedSearchQuery
//       ? filteredByType.filter((event) =>
//           event.eventName.toLowerCase().includes(trimmedSearchQuery)
//         )
//       : filteredByType;

//     setFilteredEvents(fullyFilteredEvents);
//   }, [eventData, filter, searchQuery]);

//   // Filter options
//   const filterOptions = [
//     { value: "All", label: "All Events" },
//     { value: "Ongoing", label: "Ongoing" },
//     { value: "Upcoming", label: "Upcoming" },
//     { value: "Completed", label: "Completed" },
//   ];

//   return (
//     <div className="p-4">
//       <div className="flex items-center justify-between mb-6 space-x-4">
//         {/* Create Button */}
//         {!Dashboard && (
//           <button
//             onClick={handleCreateEvent}
//             className="flex items-center bg-blue-500 text-white py-2 px-4 rounded-lg shadow-lg hover:bg-blue-600 transition"
//           >
//             <FaPlusCircle className="mr-2" />
//             Create Event
//           </button>
//         )}

//         {/* Filter Select */}
//         <Select
//           options={filterOptions}
//           value={filter}
//           onChange={(selectedOption) => setFilter(selectedOption)}
//           className="w-40"
//         />

//         {/* Redesigned YouTube-style Search Input */}
//         <div className="relative w-full lg:w-1/2">
//           <div className="flex items-center bg-white border border-gray-300 rounded-full shadow-sm hover:bg-opacity-90 focus-within:ring-2 focus-within:ring-blue-400 transition">
//             {/* Input Field */}
//             <input
//               type="text"
//               placeholder="Search events..."
//               className="w-full text-gray-700 bg-transparent placeholder-gray-500 focus:outline-none px-4 py-3 rounded-full"
//               value={searchQuery}
//               onChange={(e) => setSearchQuery(e.target.value)}
//             />

//             {/* Clear Icon */}
//             {searchQuery && (
//               <button
//                 onClick={() => setSearchQuery("")}
//                 className="text-gray-500 h-full px-3 flex items-center transition"
//                 style={{
//                   position: "absolute",
//                   right: "45px",
//                   top: 0,
//                   bottom: 0,
//                 }}
//               >
//                 <FaTimes />
//               </button>
//             )}

//             {/* Vertical Line Separator */}
//             <div
//               className="h-full border-l border-gray-300"
//               style={{
//                 position: "absolute",
//                 right: "40px",
//                 top: "50%",
//                 transform: "translateY(-50%)",
//               }}
//             ></div>

//             {/* Search Icon */}
//             <button
//               onClick={() => setFilteredEvents(filteredEvents)}
//               className="text-gray-500 hover:bg-gray-200 h-full px-3 flex items-center transition rounded-r-full"
//               style={{
//                 position: "absolute",
//                 right: "0px",
//                 top: 0,
//                 bottom: 0,
//               }}
//             >
//               <FaSearch />
//             </button>
//           </div>
//         </div>
//       </div>

//       {/* Event Cards */}
//       <div className="grid grid-cols-1 gap-6">
//         {filteredEvents.map((event, index) => (
//           <div
//             key={event.index}
//             className="relative transition transform rounded-lg overflow-hidden bg-white"
//           >
//             {/* Serial Number
//             <div className="absolute top-1 left-2 bg-gray-800 hover:bg-blue-600 hover:cursor-pointer text-gray-50 text-[8px] w-5 h-5 flex items-center justify-center rounded-full font-semibold">
//               #{index + 1}
//             </div> */}

//             <EventCardParent events={filteredEvents} />
//           </div>
//         ))}
//       </div>

//       {/* Scroll Button */}
//       {showScrollButton && (
//         <button
//           onClick={scrollToTop}
//           className="fixed bottom-4 right-4 transform -translate-x-1/2 bg-gray-200 bg-opacity-70 text-gray-700 p-3 rounded-full shadow-lg hover:bg-gray-300 transition"
//           aria-label="Scroll to Top"
//         >
//           <FaArrowUp />
//         </button>
//       )}
//     </div>
//   );
// };

// export default EventTable;
